import React, { useEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createBrowserHistory } from 'history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import DateFnsUtils from '@date-io/date-fns';
import { SnackbarProvider } from 'notistack';
import {
    createStyles,
    createTheme,
    jssPreset,
    makeStyles,
    StylesProvider,
    ThemeProvider,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
    Auth,
    CacheProvider,
    PORTAL_TYPE,
    Routes,
    ScrollReset,
    setApplicationDetails,
    SettingsNotification,
    useSettings,
    useInitialTheme,
    setUserData,
} from '@nsd/fe';
import insightQoreService from 'src/services/insightQoreService';
import hasAccess, { getRolesByEnv } from 'src/utils/hasAccess';
import apps from 'src/apps';
import Chart from 'chart.js/auto'; //DO NOT REMOVE
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale } from 'chart.js';


ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale);
const APP_VERSION = process.env.REACT_APP_VERSION;
const APP_NAME = 'Insights';
const APP_END_USER = PORTAL_TYPE.EMPLOYEE;
/* Used to determine loading/splash screen logo
Choices for APP_INTERNAL_NAME
[
  "dispatch-qore",
  "lender-qore",
  "client-qore",
  "provider-qore",
  "claims-qore",
  "contact-qore",
  "insight-qore",
  "sales-qore",
  "marketing-qore",
  "market-qore",
  "developer-qore",
  "pricebook-qore",
  "cash-qore",
  "integration-qore",
  "driver-qore",
  "billing-qore",
  "cash-qore",
  "dealer-qore",
  "licensing-qore",
  "cash-qore",
  "data-qore",
  "notifications-qore"
]
*/
const APP_INTERNAL_NAME = 'insight-qore';


const history = createBrowserHistory();
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

const useStyles = makeStyles(() =>
    createStyles({
        '@global': {
            '*': {
                boxSizing: 'border-box',
                margin: 0,
                padding: 0,
            },
            html: {
                '-webkit-font-smoothing': 'antialiased',
                '-moz-osx-font-smoothing': 'grayscale',
                height: '100%',
                width: '100%',
            },
            body: {
                height: '100%',
                width: '100%',
                scrollbarWidth: 'none', // Firefox
            },
            '#root': {
                height: '100%',
                width: '100%',
            },
            '*::-webkit-scrollbar': {
                width: '0px',
                height: '0px',
                background: 'transparent',
            },
        },
    })
);

function App() {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(
            setApplicationDetails(
                APP_END_USER,
                APP_NAME,
                APP_VERSION,
                APP_INTERNAL_NAME
            )
        );
    }, [dispatch]);

    const { user, appSettings } = useSelector(state => state.account);

    let [hasLoadedInitialMembers, setHasLoadedInitialMembers] = useState(false);
    const { settings, saveSettings } = useSettings();
    const [didSaveInitialTheme, setDidSaveInitialTheme] = useState(false);

    useEffect(() => {
        (async () => {
            if (user && !hasLoadedInitialMembers) {
                setHasLoadedInitialMembers(true);

                let hasAssociatedMembers = false
                if (hasAccess(user.roles, getRolesByEnv().INSIGHT_QORE.TEAM_MEMBER)) {

                    const teamMemberAttributes = await insightQoreService.getSalesTeamMemeberAttributeByFullName(
                        user.fullName
                    );
                    if (teamMemberAttributes) {
                        let associatedTeamMembers = await insightQoreService.getAssociatedTeamMemberById(
                            teamMemberAttributes.salesTeamId
                        );
                        if (associatedTeamMembers && associatedTeamMembers.length > 0) {
                            hasAssociatedMembers = true;
                        }

                    }
                }

                await dispatch(
                    setUserData(
                        { ...user, hasAssociatedMembers },
                        appSettings,
                        user.roles,
                        null
                    )
                );

            }
        })();
    }, [user]);

    useEffect(() => {
        if (settings && saveSettings && !didSaveInitialTheme) {
            /**
             * TODO: This is a temporary workaround to set theme to the 
             * new "NSD Light" theme. In the future, the "Light"
             * theme will be updated to match the new "NSD Light" theme
             * and this will no longer be needed.
             */
            saveSettings({
                ...settings,
                theme: 'NSD LIGHT',
            });
            setDidSaveInitialTheme(true);
            console.warn('Default theme set to NSD LIGHT. This will be updated in the future.');
        }
    }, [saveSettings, didSaveInitialTheme, settings]);


    useStyles();
    const { theme: baseTheme } = useInitialTheme();

    // Apply NSD brand colors on top of the base theme from the API / fallback
    const theme = baseTheme
        ? createTheme(baseTheme, {
            palette: {
                primary: {
                    main: '#2648f1',       // NSD brand blue
                    contrastText: '#ffffff',
                },
                secondary: {
                    main: '#004751',       // NSD dark teal
                    contrastText: '#ffffff',
                },
                success: {
                    main: '#88f731',       // NSD green
                },
                background: {
                    default: '#faf1ee',    // NSD cream
                    paper: '#ffffff',
                },
            },
        })
        : null;

    if (!theme) return null;
    return (
        <CacheProvider>
            <DndProvider backend={HTML5Backend}>
                <ThemeProvider theme={theme}>
                    <StylesProvider jss={jss}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <SnackbarProvider maxSnack={1}>
                                <Router history={history}>
                                    <Auth>
                                        <ScrollReset />
                                        {/* Make the link here open the settings widget? */}
                                        <SettingsNotification />
                                        <Routes
                                            apps={apps || []}
                                        />
                                    </Auth>
                                </Router>
                            </SnackbarProvider>
                        </MuiPickersUtilsProvider>
                    </StylesProvider>
                </ThemeProvider>
            </DndProvider>
        </CacheProvider>
    );
}

export default App;

